import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PaymentEntry, getPayments } from '../../api/paymentsApi';
import { CollectionLiabilityEntry, getCollectionLiabilities } from '../../api/collectionLiabilitiesApi';
import { DataTable } from '../../components/ui/DataTable';
import { PaymentRow, PaymentsTableHeader } from './payments/PaymentsListView';
import { LiabilityRow, TableHeader as CollectionsTableHeader } from './collections/CollectionsListView';

const PREVIEW_SIZE = 5;

export default function ParentHomeView() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [payments, setPayments] = useState<PaymentEntry[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(true);

  const [liabilities, setLiabilities] = useState<CollectionLiabilityEntry[]>([]);
  const [liabilitiesLoading, setLiabilitiesLoading] = useState(true);

  useEffect(() => {
    getPayments({ page: 0, size: PREVIEW_SIZE, sortOrder: 'DESC' })
      .then((res) => setPayments(res.rows))
      .finally(() => setPaymentsLoading(false));
  }, []);

  useEffect(() => {
    getCollectionLiabilities({ page: 0, size: PREVIEW_SIZE })
      .then((res) => setLiabilities(res.rows))
      .finally(() => setLiabilitiesLoading(false));
  }, []);

  return (
    <Box sx={(theme) => ({ p: 4, width: '100%', flex: 1, backgroundColor: theme.app.mutedBackground })}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 2 }}>
            {t('sidebar.payments')}
          </Typography>
          {paymentsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : payments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('start.noPayments')}
            </Typography>
          ) : (
            <DataTable>
              <PaymentsTableHeader />
              {payments.map((payment) => (
                <PaymentRow key={payment.id} payment={payment} />
              ))}
            </DataTable>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button onClick={() => navigate('/parent/payments')}>{t('start.viewAll')}</Button>
          </Box>
        </Box>

        <Box>
          <Typography variant="h4" sx={{ mb: 2 }}>
            {t('sidebar.collections')}
          </Typography>
          {liabilitiesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : liabilities.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('start.noCollections')}
            </Typography>
          ) : (
            <DataTable>
              <CollectionsTableHeader />
              {liabilities.map((liability) => (
                <LiabilityRow
                  key={liability.id}
                  liability={liability}
                  onClick={() => navigate(`/parent/collections/${liability.id}`)}
                  onPay={() => navigate(`/parent/execute-payment?liabilityId=${liability.id}`)}
                />
              ))}
            </DataTable>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button onClick={() => navigate('/parent/collections')}>{t('start.viewAll')}</Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
